import _ from "lodash";
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp, FileText, Layout, TrendingUp, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/rootReducer";
import useAxios from "../../../../hooks/useAxios";
import { IAnswerDistributionItem, IGetAIAnalysisGuide, IGetCurrentSemesterExams, IGetExamDetail, IGetExamTrend, IGetExamTrendLT, IGetLearningDiagnosis, IGetSkillAnalysis, IGetStudentInfoWithAbsence, IGetWrongAnswerAnalysis, IParsedWrongAnswer, IQuizChoice, ISkillAnalysisRecord, IStudentInfo, IWrongAnswerDetail } from "../../../../models/aiCare.model";
import { getExamDetailData, getExamTrendMTData, getExamTrendLTData, getExamCurrentSemesterData, getSkillAnalysisData, getLearningDiagnosisData, getWrongAnswerAnalysisData, getAIAnalysisGuideData } from "../../../../api/aiCare.api";
import { getJwtToken } from "../../../../api/auth.api";
import Chart from "react-apexcharts";
import Modal from "./Modals";
import NextLevel from "./NextLevel";
import config from "../../../../lib/config";
import Spinner from "../../../Spinner";

// 과목 순서 정의
const SUBJECT_ORDER = ['Listening', 'Phonics', 'Reading', 'Vocabulary', 'Grammar'];

// 레벨 코드 상수
const LEVEL_CODE = {
    PENTA: 777,
    HEXA: 780,
    HEPTA: 783,
    OCTA: 786,
    NONA: 790,
    DECA: 794,
    HENDECA: 798
} as const;

// 레벨별 중점 관리 과목 가중치 및 선정 개수
const WEAK_SUBJECT_WEIGHTS: Record<number, { weights: Record<string, number>, count: number }> = {
    [LEVEL_CODE.PENTA]: {
        weights: { 'Phonics': 4, 'Reading': 2 },
        count: 1
    },
    [LEVEL_CODE.HEXA]: {
        weights: { 'Reading': 4, 'Vocabulary': 3, 'Listening': 1 },
        count: 2
    },
    [LEVEL_CODE.HEPTA]: {
        weights: { 'Reading': 4, 'Vocabulary': 3, 'Listening': 2, 'Grammar': 1 },
        count: 2
    },
    [LEVEL_CODE.OCTA]: {
        weights: { 'Reading': 4, 'Vocabulary': 3.1, 'Listening': 3, 'Grammar': 2 },
        count: 2
    },
    [LEVEL_CODE.NONA]: {
        weights: { 'Reading': 4, 'Vocabulary': 3.1, 'Grammar': 3, 'Listening': 2 },
        count: 2
    }
};

// 다음레벨 표시 Course Code AAray
const LevelDispCourseCodeArray = [779, 829, 1088, 1098, 782, 785, 789, 793, 796];

// 과목별 색상
const SUBJECT_COLORS: Record<string, string> = {
    'Overall': '#ec4899',     // 핑크
    'Listening': '#3b82f6',   // 파랑
    'Reading': '#f97316',     // 주황
    'Vocabulary': '#a855f7',  // 보라
    'Grammar': '#22c55e',     // 초록
    'Phonics': '#14b8a6',     // 청록(teal)
};

interface PopupData {
    type: 'WRONG_ANSWER' | null; 
    data?: {
        exam_test_code: string;
        skill_code: number;
    };
}

// 시험 정보 인터페이스
interface IExamTestCodeInfo {
    exam_test_code: string;
    learning_period_start: string;
    learning_period_end: string;
}

// Accordion 상태 타입
interface AccordionState {
    chart: boolean;
    skill: boolean;
    diagnosis: boolean;
    summary: boolean;
    nextLevel: boolean;
    counseling: boolean;
}

interface AnalysisProps {
    memberCode: string;
    isMT3Level: boolean;
    accordions: AccordionState;
    setAccordions: React.Dispatch<React.SetStateAction<AccordionState>>;
    studentInfo: IStudentInfo;
    jwtToken?: string;
    setIsNextLevelOpen: (isNextLevelOpen: boolean) => void;
}

const Analysis = ({ memberCode, isMT3Level, accordions, setAccordions, studentInfo, jwtToken, setIsNextLevelOpen}: AnalysisProps) => {
    const edubaseUrl = config.edubaseUrl;
    const { auth } = useSelector((state: RootState) => state.auth);

    const [activeTab, setActiveTab] = useState('MT1');
    const [activePopup, setActivePopup] = useState<PopupData>({ type: null });
    
    // 탭별 exam_test_code 저장
    const [examTestCodes, setExamTestCodes] = useState<{
        MT1: IExamTestCodeInfo | null;
        MT2: IExamTestCodeInfo | null;
        MT3: IExamTestCodeInfo | null;
        TT: IExamTestCodeInfo | null;
    }>({ MT1: null, MT2: null, MT3: null, TT: null });

    // 탭별 데이터 캐시 (exam_test_code를 키로 사용)
    type TabDataCache = {
        examDetail: IGetExamDetail | null;
        trendMT: IGetExamTrend | null;
        trendLT: IGetExamTrendLT | null;
        skillAnalysis: IGetSkillAnalysis | null;
        learningDiagnosis: IGetLearningDiagnosis | null;
        aiAnalysisGuide: IGetAIAnalysisGuide | null;
    };
    const [dataCache, setDataCache] = useState<Record<string, TabDataCache>>({});

    // 시험 조회 타입(M, T, A) - M : MT3, T : TT, A : ALL
    const [examTabType, setExamTabType] = useState('');

    // 시험 상세 데이터 Title
    const [examDetailTitle, setExamDetailTitle] = useState('');

    // 시섬 성적 변화 추이 LT Title
    const [examTrendLTTitle, setExamTrendLTTitle] = useState('');

    // 현재 학기 시험 정보 조회
    const { data: examCurrentSemesterData, execute: executeExamCurrentSemester } = useAxios<IGetCurrentSemesterExams>();

    // 시험 상세 데이터 조회
    const { data: examDetailData, execute: executeExamDetail } = useAxios<IGetExamDetail>();
    
    // 시험 성적 변화 추이 조회 (MT)
    const { data: examTrendMTData, execute: executeExamTrendMT } = useAxios<IGetExamTrend>();
    
    // 시험 성적 변화 추이 조회 (LT)
    const { data: examTrendLTData, execute: executeExamTrendLT } = useAxios<IGetExamTrendLT>();

    // 스킬 분석 조회
    const { data: skillAnalysisData, execute: executeSkillAnalysis } = useAxios<IGetSkillAnalysis>();

    // 학습 종합 진단 & 개선 전략 조회
    const { data: learningDiagnosisData, isLoading: learningDiagnosisLoading, execute: executeLearningDiagnosis } = useAxios<IGetLearningDiagnosis>();

    // 오답 문항 상세 분석 조회
    const { data: wrongAnswerData, execute: executeWrongAnswer } = useAxios<IGetWrongAnswerAnalysis>();

    // AI 총평 및 데이터 해석 상담 가이드 조회
    const { data: aiAnalysisGuideData, isLoading: aiAnalysisGuideLoading, execute: executeAIAnalysisGuide } = useAxios<IGetAIAnalysisGuide>();

    // 오답 문항 팝업 열릴 때 API 호출
    useEffect(() => {
        if (activePopup.type === 'WRONG_ANSWER' && activePopup.data) {
            const { exam_test_code, skill_code } = activePopup.data;
            executeWrongAnswer(getWrongAnswerAnalysisData(exam_test_code, skill_code));
        }
    }, [activePopup, jwtToken]);

    // 페이지 로드 시 현재 학기 시험 정보 조회
    useEffect(() => {
        if (memberCode && jwtToken) {
            executeExamCurrentSemester(getExamCurrentSemesterData(memberCode, jwtToken));
        }
    }, [memberCode, jwtToken]);

    // 현재 학기 시험 정보로 examTestCodes 설정
    useEffect(() => {
        if (examCurrentSemesterData?.exams) {
            const codes: { MT1: IExamTestCodeInfo | null; MT2: IExamTestCodeInfo | null; MT3: IExamTestCodeInfo | null; TT: IExamTestCodeInfo | null } = {
                MT1: null,
                MT2: null,
                MT3: null,
                TT: null
            };

            let isAllExamType = false;

            const createExamInfo = (exam: typeof examCurrentSemesterData.exams[0]): IExamTestCodeInfo => ({
                exam_test_code: exam.exam_test_code,
                learning_period_start: exam.learning_period_start,
                learning_period_end: exam.learning_period_end
            });

            examCurrentSemesterData.exams.forEach(exam => {
                if (exam.exam_div === 'MT') {
                    // exam_name에서 MT1, MT2, MT3 추출
                    const mtNumber = extractMTNumberFromName(exam.exam_name);
                    if (mtNumber === 'MT1') codes.MT1 = createExamInfo(exam);
                    else if (mtNumber === 'MT2') codes.MT2 = createExamInfo(exam);
                    else if (mtNumber === 'MT3') {
                        codes.MT3 = createExamInfo(exam);
                        if(!isMT3Level) {
                            //MT3레벨이 아닌데 MT3 시험이 있는 경우 처리
                            setExamTabType('A');
                            isAllExamType = true;
                        }
                    }
                } else if (exam.exam_div === 'LT') {
                    codes.TT = createExamInfo(exam);
                    if(isMT3Level) {
                        //MT3레벨인데 LT(TT) 시험이 있는 경우 처리
                        setExamTabType('A');
                        isAllExamType = true;
                    }
                }
            });

            if(!isAllExamType) {
                if(isMT3Level) {
                    setExamTabType('M');
                } else {
                    setExamTabType('T');
                }
            }
            
            setExamTestCodes(codes);
        }
    }, [examCurrentSemesterData]);



    // 탭 변경 시 해당 exam_test_code로 상세 조회 및 Trend 조회 (캐싱 적용)
    useEffect(() => {
        const fetchTabData = async () => {
            const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
            
            if (examInfo?.exam_test_code && auth.memberCode) {
                const examTestCode = examInfo.exam_test_code;
                const cached = dataCache[examTestCode];

                let newJwtToken = '';
                if(!cached || !cached.examDetail || !cached.skillAnalysis || !cached.learningDiagnosis ||
                     (activeTab === 'TT' && !cached.trendLT) || (activeTab !== 'TT' && !cached.trendMT)) {
                    // jwtToken 가져오기
                    const jwtTokenResponse = await getJwtToken({
                        adminMemCode: auth.memberCode,
                        studentMemCode: memberCode,
                    });
                    newJwtToken = jwtTokenResponse.data.data;
                }
                
                // 캐시가 없거나 일부 데이터가 없으면 API 호출
                if (!cached || !cached.examDetail) {
                    executeExamDetail(getExamDetailData(examTestCode, newJwtToken));
                }
                
                if (!cached || !cached.skillAnalysis) {
                    executeSkillAnalysis(getSkillAnalysisData(examTestCode, newJwtToken));
                }
                
                if (activeTab === 'TT') {
                    if (!cached || !cached.trendLT) {
                        executeExamTrendLT(getExamTrendLTData(examTestCode, memberCode, newJwtToken));
                    }
                } else {
                    if (!cached || !cached.trendMT) {
                        executeExamTrendMT(getExamTrendMTData(examTestCode, memberCode, newJwtToken));
                    }
                }

                if (!cached || !cached.learningDiagnosis) {
                    executeLearningDiagnosis(getLearningDiagnosisData(examTestCode, examInfo.learning_period_start, examInfo.learning_period_end, newJwtToken));
                }

                if (!cached || !cached.aiAnalysisGuide) {
                    executeAIAnalysisGuide(getAIAnalysisGuideData(examTestCode, newJwtToken));
                }
            }
        };
        
        fetchTabData();
    }, [activeTab, examTestCodes, memberCode, memberCode]);

    // API 응답을 캐시에 저장
    useEffect(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return;

        const examTestCode = examInfo.exam_test_code;
        const currentCache = dataCache[examTestCode] || {
            examDetail: null,
            trendMT: null,
            trendLT: null,
            skillAnalysis: null,
            learningDiagnosis: null
        };

        let updated = false;
        const newCache = { ...currentCache };

        // examDetail 캐시 저장
        if (examDetailData && examDetailData.exam_test_code === examTestCode && !currentCache.examDetail) {
            newCache.examDetail = examDetailData;
            updated = true;
        }

        // trendMT 캐시 저장
        if (examTrendMTData && examTrendMTData.exam_test_code === examTestCode && !currentCache.trendMT) {
            newCache.trendMT = examTrendMTData;
            updated = true;
        }

        // trendLT 캐시 저장
        if (examTrendLTData && examTrendLTData.exam_test_code === examTestCode && !currentCache.trendLT) {
            newCache.trendLT = examTrendLTData;
            updated = true;
        }

        // skillAnalysis 캐시 저장
        if (skillAnalysisData && skillAnalysisData.exam_test_code === examTestCode && !currentCache.skillAnalysis) {
            newCache.skillAnalysis = skillAnalysisData;
            updated = true;
        }

        // learningDiagnosis 캐시 저장
        if (learningDiagnosisData && learningDiagnosisData.exam_test_code === examTestCode && !currentCache.learningDiagnosis) {
            newCache.learningDiagnosis = learningDiagnosisData;
            updated = true;
        }

        // aiAnalysisGuide 캐시 저장
        if (aiAnalysisGuideData && aiAnalysisGuideData.exam_test_code === examTestCode && !currentCache.aiAnalysisGuide) {
            newCache.aiAnalysisGuide = aiAnalysisGuideData;
            updated = true;
        }

        if (updated) {
            setDataCache(prev => ({
                ...prev,
                [examTestCode]: newCache
            }));
        }
    }, [examDetailData, examTrendMTData, examTrendLTData, skillAnalysisData, learningDiagnosisData, examTestCodes, activeTab, dataCache, aiAnalysisGuideData]);

    // 현재 탭의 캐시된 데이터 가져오기
    const currentCachedData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return dataCache[examInfo.exam_test_code] || null;
    }, [activeTab, examTestCodes, dataCache]);

    // 렌더링에 사용할 데이터 (캐시 우선, 없으면 API 응답)
    const displayExamDetailData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return currentCachedData?.examDetail || (examDetailData?.exam_test_code === examInfo.exam_test_code ? examDetailData : null);
    }, [currentCachedData, examDetailData, activeTab, examTestCodes]);

    const displayTrendMTData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return currentCachedData?.trendMT || (examTrendMTData?.exam_test_code === examInfo.exam_test_code ? examTrendMTData : null);
    }, [currentCachedData, examTrendMTData, activeTab, examTestCodes]);

    const displayTrendLTData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return currentCachedData?.trendLT || (examTrendLTData?.exam_test_code === examInfo.exam_test_code ? examTrendLTData : null);
    }, [currentCachedData, examTrendLTData, activeTab, examTestCodes]);

    const displaySkillAnalysisData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return currentCachedData?.skillAnalysis || (skillAnalysisData?.exam_test_code === examInfo.exam_test_code ? skillAnalysisData : null);
    }, [currentCachedData, skillAnalysisData, activeTab, examTestCodes]);

    const displayLearningDiagnosisData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return currentCachedData?.learningDiagnosis || (learningDiagnosisData?.exam_test_code === examInfo.exam_test_code ? learningDiagnosisData : null);
    }, [currentCachedData, learningDiagnosisData, activeTab, examTestCodes]);

    const displayAIAnalysisGuideData = useMemo(() => {
        const examInfo = examTestCodes[activeTab as keyof typeof examTestCodes];
        if (!examInfo?.exam_test_code) return null;
        return currentCachedData?.aiAnalysisGuide || (aiAnalysisGuideData?.exam_test_code === examInfo.exam_test_code ? aiAnalysisGuideData : null);
    }, [currentCachedData, aiAnalysisGuideData, activeTab, examTestCodes]);

    useEffect(() => {
        if (displayExamDetailData) {
            setExamDetailTitle(displayExamDetailData.exam_name);
        }
    }, [displayExamDetailData]);

    useEffect(() => {
        if (displayTrendLTData) {
            setExamTrendLTTitle(displayTrendLTData?.previous_exam?.exam_name);
        }
    }, [displayTrendLTData]);

    useEffect(() => {
        if (wrongAnswerData && wrongAnswerData.length > 0) {
            //console.log('wrongAnswerData:', wrongAnswerData);
            const parsed = wrongAnswerData.map(item => parseQuizXml(item));
            // questionNum 순서대로 정렬
            parsed.sort((a, b) => parseInt(a.questionNum || '0', 10) - parseInt(b.questionNum || '0', 10));
            //console.log('parsedWrongAnswerData:', parsed);
            setParsedWrongAnswerData(parsed);
        }
    }, [wrongAnswerData]);

    /*
    useEffect(() => {
        if (skillAnalysisData) {
            console.log(skillAnalysisData);
        }
    }, [skillAnalysisData]);
    */

    // examTabType에 따른 탭 목록
    const examTabs = useMemo(() => {
        switch (examTabType) {
            case 'M':
                return ['MT1', 'MT2', 'MT3'];
            case 'T':
                return ['MT1', 'MT2', 'TT'];
            case 'A':
                return ['MT1', 'MT2', 'MT3', 'TT'];
            default:
                return ['MT1', 'MT2', 'TT'];  // 기본값
        }
    }, [examTabType]);

    // exam_name에서 MT 번호 추출 (예: "2025 가을학기 Hepta 1 MT1" -> "MT1")
    const extractMTNumberFromName = (examName: string): string | null => {
        const match = examName.match(/MT(\d+)/i);
        return match ? `MT${match[1]}` : null;
    };

    // 파싱된 오답 문항 데이터 상태
    const [parsedWrongAnswerData, setParsedWrongAnswerData] = useState<IParsedWrongAnswer[]>([]);

    // XML 파싱 함수
    const parseQuizXml = (item: IWrongAnswerDetail): IParsedWrongAnswer => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(item.quiz_xml, 'text/xml');

        // QUIZ 태그에서 정보 추출
        const quizElement = xmlDoc.querySelector('QUIZ');
        const questionNum = quizElement?.getAttribute('num') || '';
        const correctAnswerAttr = quizElement?.getAttribute('correctAnswer') || item.quiz_answer;

        // Direction 추출 (여러 CHAR 요소의 텍스트 합치기)
        const directionTextElements = xmlDoc.querySelectorAll('DIRECTION > TEXT');
        let direction = '';
        directionTextElements.forEach(textEl => {
            const charElements = textEl.querySelectorAll('CHAR');
            charElements.forEach(charEl => {
                // 이미지가 없는 CHAR에서만 텍스트 추출
                if (!charEl.querySelector('IMAGE') && charEl.textContent) {
                    direction += charEl.textContent;
                }
            });
        });
        direction = direction.trim();

        // Context 추출 (여러 TEXT 요소) - HEADERPART 안에 있음
        const contextElements = xmlDoc.querySelectorAll('CONTEXT > TEXT');
        const context: string[] = [];
        let contextImage: string | undefined;
        let imageType: 'header' | 'problem' | undefined;

        contextElements.forEach(textEl => {
            const charElements = textEl.querySelectorAll('CHAR');
            let line = '';

            charElements.forEach(charEl => {
                const imageEl = charEl.querySelector('IMAGE');
                if (imageEl) {
                    contextImage = imageEl.getAttribute('src') || undefined;
                    imageType = 'header'; // CONTEXT(HEADERPART) 내 이미지
                    return;
                }

                if (charEl.textContent) {
                    // non-breaking space (&#160;, \u00A0)를 일반 공백으로 변환
                    const text = charEl.textContent.replace(/\u00A0/g, ' ');
                    line += text;
                }
            });

            const trimmed = line.trim();
            if (trimmed) context.push(trimmed);
        });

        // Question 추출 (여러 TEXT 요소 순회, 각 TEXT 내 여러 CHAR 처리) - PROBLEMPART 안에 있음
        const questionElements = xmlDoc.querySelectorAll('QUESTION > TEXT');
        let question = '';
        questionElements.forEach(textEl => {
            // TEXT 안의 모든 CHAR 요소 순회
            const charElements = textEl.querySelectorAll('CHAR');
            charElements.forEach(charEl => {
                // 이미지가 없는 CHAR에서만 텍스트 추출
                if (!charEl.querySelector('IMAGE')) {
                    if (charEl.textContent) {
                        // non-breaking space (&#160;, \u00A0)를 일반 공백으로 변환
                        const text = charEl.textContent.replace(/\u00A0/g, ' ');
                        question += text;
                    }
                }
                // QUESTION 내 이미지도 파싱 (contextImage가 없을 경우)
                const imageEl = charEl.querySelector('IMAGE');
                if (imageEl && !contextImage) {
                    contextImage = imageEl.getAttribute('src') || undefined;
                    imageType = 'problem';  // QUESTION(PROBLEMPART) 내 이미지
                }
            });
        });
        question = question.trim();

        // Choices 추출
        const choiceElements = xmlDoc.querySelectorAll('CHOICE ITEM');
        const choices: IQuizChoice[] = [];

        // a=1, b=2, c=3, d=4 매핑
        const answerToNum = (answer: string): number => {
            const map: Record<string, number> = { 'a': 1, 'b': 2, 'c': 3, 'd': 4 };
            return map[answer.toLowerCase()] || 0;
        };

        const correctNum = answerToNum(item.quiz_answer);
        const userNum = answerToNum(item.user_answer);

        choiceElements.forEach(itemEl => {
            const num = parseInt(itemEl.getAttribute('num') || '0', 10);
            const textElements = itemEl.querySelectorAll('TEXT');
            let text = '';
            let choiceImage: string | undefined;
            
            textElements.forEach(textEl => {
                const charElements = textEl.querySelectorAll('CHAR');
                charElements.forEach(charEl => {
                    // 이미지가 있는지 확인
                    const imageEl = charEl.querySelector('IMAGE');
                    if (imageEl) {
                        choiceImage = imageEl.getAttribute('src') || undefined;
                    } else if (charEl.textContent) {
                        // 이미지가 없는 CHAR에서만 텍스트 추출
                        text += charEl.textContent;
                    }
                });
            });
            text = text.trim();

            choices.push({
                num,
                text,
                image: choiceImage,
                isCorrect: num === correctNum,
                isUserAnswer: num === userNum
            });
        });

        // Explanation 추출
        const explainElement = xmlDoc.querySelector('EXPLAIN TEXT CHAR');
        const explanation = explainElement?.textContent?.trim() || undefined;

        // Answer Distribution 추출
        const answerDistribution = item.answer_distribution ? JSON.parse(item.answer_distribution) : undefined;
        const answerDistributionParsed = answerDistribution ? answerDistribution.map((item: IAnswerDistributionItem) => ({
            answer: item.answer,
            rate: item.rate
        })) : undefined;

        return {
            // 메타 정보
            exam_test_code: item.exam_test_code,
            subject_name: item.subject_name,
            skill_name: item.skill_name,
            skill_eng_name: item.skill_name_eng,
            indicator_name: item.indicator_name,
            indicator_eng_name: item.indicator_name_eng,
            difficulty_level: item.difficulty_level,

            // 파싱된 데이터
            direction,
            context: context.length > 0 ? context : undefined,
            contextImage,
            imageType,
            questionNum,
            question,
            choices,
            correctAnswer: item.quiz_answer,
            userAnswer: item.user_answer,
            explanation,
            answerDistribution,
            answerDistributionParsed
        };
    };

    // 과목별로 스킬 데이터 그룹화
    const skillData = displaySkillAnalysisData;
    
    const groupedSkillData = useMemo(() => {
        if (!skillData?.skills) return {};
        
        const grouped: Record<string, typeof skillData.skills> = {};
        
        skillData.skills.forEach(skill => {
            if (!grouped[skill.subject_name]) {
                grouped[skill.subject_name] = [];
            }
            grouped[skill.subject_name].push(skill);
        });
        
        return grouped;
    }, [skillData]);

    // 스킬 데이터가 있는 과목 목록 (탭용)
    const availableSkillSubjects = useMemo(() => {
        const subjects = Object.keys(groupedSkillData).filter(subject => groupedSkillData[subject].length > 0);
        // SUBJECT_ORDER 순서대로 정렬 (정의되지 않은 과목은 맨 뒤)
        return subjects.sort((a, b) => {
            const indexA = SUBJECT_ORDER.indexOf(a);
            const indexB = SUBJECT_ORDER.indexOf(b);
            // SUBJECT_ORDER에 없으면 맨 뒤로
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }, [groupedSkillData]);

    // 과목 데이터를 tableOrder 기준으로 정렬하는 함수
    const sortSubjectsByTableOrder = (subjects: any[]) => {
        const tableOrder = ['Overall', ...SUBJECT_ORDER];
        return [...subjects].sort((a, b) => {
            const indexA = tableOrder.indexOf(a.subject_name);
            const indexB = tableOrder.indexOf(b.subject_name);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    };

    // Overall 계산 및 테이블 데이터 생성
    const getTableDataWithOverall = () => {
        if (!displayExamDetailData?.subjects) return [];
        
        const subjects = displayExamDetailData.subjects;
        const tableOrder = ['Overall', ...SUBJECT_ORDER];

        // SUBJECT_ORDER(+Overall) 기준으로 정렬 (정의되지 않은 과목은 맨 뒤)
        return [...subjects].sort((a, b) => {
            const indexA = tableOrder.indexOf(a.subject_name);
            const indexB = tableOrder.indexOf(b.subject_name);

            if (indexA === -1 && indexB === -1) return a.subject_name.localeCompare(b.subject_name);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    };

    // 선택된 과목 (체크박스)
    const [selectedSubjects, setSelectedSubjects] = useState<string[]>(['Overall', 'Listening', 'Reading', 'Vocabulary', 'Grammar', 'Phonics']);

    // 과목 토글
    const toggleSubject = (subject: string) => {
        setSelectedSubjects(prev => 
            prev.includes(subject) 
                ? prev.filter(s => s !== subject) 
                : [...prev, subject]
        );
    };

    // exam_name에서 MT 번호 추출 (예: "2025 봄학기 Hexa 2 MT1" -> "MT1")
    const extractMTFromExamName = (examName: string): string => {
        const match = examName.match(/MT(\d+)/i);
        return match ? `MT${match[1]}` : '';
    };

    // 학기명 축약 함수 (예: "2024 WINTER" -> "24 Win")
    const formatSemesterShort = (semesterName: string): string => {
        if (!semesterName) return '';
        const parts = semesterName.split(' ');
        if (parts.length < 2) return semesterName;
        const year = parts[0].slice(-2); // "2024" -> "24"
        const seasonMap: Record<string, string> = {
            'SPRING': 'Spr',
            'SUMMER': 'Sum',
            'FALL': 'Fall',
            'WINTER': 'Win'
        };
        const season = seasonMap[parts[1]] || parts[1];
        return `${year} ${season}`;
    };

    // 차트 데이터 생성
    const getChartData = () => {
        if (!displayTrendMTData?.trends || displayTrendMTData.trends.length === 0) {
            return { categories: [], series: [], availableSubjects: [] };
        }

        const trends = displayTrendMTData.trends;
        
        // 고유한 시험들 추출 (학기 + exam_name 기준으로 그룹화)
        const examMap = new Map<string, { semester_name: string; semester_div: string; exam_name: string; exam_test_code: string }>();
        trends.forEach(t => {
            const mtNumber = extractMTFromExamName(t.exam_name);
            const key = `${t.learning_year_code}_${t.semester_div}_${mtNumber}`;
            if (!examMap.has(key)) {
                examMap.set(key, {
                    semester_name: t.semester_name,
                    semester_div: t.semester_div,
                    exam_name: t.exam_name,
                    exam_test_code: t.exam_test_code
                });
            }
        });

        // 정렬: 년도 + 학기 + MT번호 순
        const sortedExams = Array.from(examMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
        
        // X축 카테고리 생성 (예: "25 Sum MT1")
        const categories = sortedExams.map(([_, exam]) => {
            const semesterShort = formatSemesterShort(exam.semester_name);
            const mtNumber = extractMTFromExamName(exam.exam_name);
            return `${semesterShort} ${mtNumber}`;
        });

        // 과목별로 데이터 그룹화
        const subjectData: Record<string, (number | null)[]> = {
            'Overall': [],
            'Phonics': [],
            'Listening': [],
            'Reading': [],
            'Vocabulary': [],
            'Grammar': [],
        };

        sortedExams.forEach(([key]) => {
            const [learningYearCode, semesterDiv, mtNumber] = key.split('_');
            
            Object.keys(subjectData).forEach(subject => {
                const record = trends.find(t => 
                    t.learning_year_code === learningYearCode && 
                    t.semester_div === semesterDiv && 
                    extractMTFromExamName(t.exam_name) === mtNumber &&
                    t.subject_name === subject
                );
                // 값이 없으면 null, 0이면 0, 값이 있으면 해당 값
                if (record && record.score_percentage != null) {
                    subjectData[subject].push(record.score_percentage);
                } else if (record && record.score_percentage === 0) {
                    subjectData[subject].push(0);
                } else {
                    subjectData[subject].push(null);
                }
            });
        });

        // 데이터가 있는 과목만 필터링 (null이 아니고 0보다 큰 값이 있는 과목)
        const availableSubjects = Object.entries(subjectData)
            .filter(([_, data]) => data.some(val => val != null && val > 0))
            .map(([subject]) => subject);

        // 시리즈 생성 (선택된 과목 중 데이터가 있는 과목만)
        const series = Object.entries(subjectData)
            .filter(([subject]) => selectedSubjects.includes(subject) && availableSubjects.includes(subject))
            .map(([subject, data]) => ({
                name: subject,
                data: data,
                color: SUBJECT_COLORS[subject]
            }));

        return { categories, series, availableSubjects };
    };
    
    const [activeSkillTab, setActiveSkillTab] = useState('Listening'); // State for Skill Analysis Tab

    // availableSkillSubjects가 준비되면 첫 탭으로 자동 선택 (현재 선택이 유효하면 유지)
    useEffect(() => {
        if (!availableSkillSubjects || availableSkillSubjects.length === 0) return;
        if (!availableSkillSubjects.includes(activeSkillTab)) {
            setActiveSkillTab(availableSkillSubjects[0]);
        }
    }, [availableSkillSubjects, activeSkillTab]);

    // 현재 스킬 탭의 데이터
    const currentSkillData = groupedSkillData[activeSkillTab] || [];

    // Total 계산
    const skillTotal = useMemo(() => {
        if (currentSkillData.length === 0) return null;
        
        const totalQuestions = currentSkillData.reduce((sum, s) => sum + s.total_questions, 0);
        const totalScore = currentSkillData.reduce((sum, s) => sum + s.student_score, 0);
        const avgStudentPercentage = currentSkillData.reduce((sum, s) => sum + s.student_percentage, 0) / currentSkillData.length;
        const avgAvgPercentage = currentSkillData.reduce((sum, s) => sum + s.avg_percentage, 0) / currentSkillData.length;
        
        return {
            totalQuestions: totalScore + '/' + totalQuestions,
            totalScore: Math.round(totalScore),
            avgStudentPercentage: Math.round(avgStudentPercentage),
            avgAvgPercentage: Math.round(avgAvgPercentage)
        };
    }, [currentSkillData]);

    // 강점/약점 Indicator 계산
    const { strongIndicator, weakIndicator } = useMemo(() => {
        if (currentSkillData.length === 0) return { strongIndicator: null, weakIndicator: null };

        // indicator_name별로 그룹화하여 평균 계산
        const indicatorMap: Record<string, { studentTotal: number; avgTotal: number; count: number }> = {};
        
        currentSkillData.forEach(item => {
            const key = item.indicator_name;
            if (!indicatorMap[key]) {
                indicatorMap[key] = { studentTotal: 0, avgTotal: 0, count: 0 };
            }
            indicatorMap[key].studentTotal += item.student_percentage;
            indicatorMap[key].avgTotal += item.avg_percentage;
            indicatorMap[key].count += 1;
        });

        // 평균 계산 후 배열로 변환
        const indicators = Object.entries(indicatorMap).map(([name, data]) => ({
            indicatorName: name,
            studentPercentage: Math.round(data.studentTotal / data.count),
            averagePercentage: Math.round(data.avgTotal / data.count)
        }));

        // 정렬: averagePercentage 내림차순 → studentPercentage 오름차순 (lodash 방식)
        const sorted = _.sortBy(indicators, 'averagePercentage').reverse();
        const finalSorted = _.sortBy(sorted, 'studentPercentage');

        // 첫 번째 = 약점 (가장 낮은 studentPercentage), 마지막 = 강점 (가장 높은 studentPercentage)
        // 하나만 있을 때는 강점으로만 표시
        const strong = finalSorted.length > 0 ? finalSorted.at(-1)?.indicatorName ?? null : null;
        const weak = finalSorted.length > 1 ? finalSorted.at(0)?.indicatorName ?? null : null;

        return { strongIndicator: strong, weakIndicator: weak };
    }, [currentSkillData]);

    // Indicator rowspan 정보 계산
    const indicatorRowspanInfo = useMemo(() => {
        const rowspanMap: Record<number, number> = {}; // index -> rowspan count
        const skipRows = new Set<number>(); // 렌더링 skip할 index
        
        let i = 0;
        while (i < currentSkillData.length) {
            const current = currentSkillData[i];
            let count = 1;
            
            // 같은 indicator_name이 연속으로 몇 개인지 확인
            while (i + count < currentSkillData.length && 
                   currentSkillData[i + count].indicator_name === current.indicator_name) {
                skipRows.add(i + count);
                count++;
            }
            
            rowspanMap[i] = count;
            i += count;
        }
        
        return { rowspanMap, skipRows };
    }, [currentSkillData]);

    const toggleAccordion = (key: keyof typeof accordions) => {
        setAccordions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 스킬 유형 분석 테이블 렌더링
    const renderSkillTable = () => {
        //if (!currentSkillData.length) return null;
        if(skillTotal === null) return null;

        const data = {
            total: skillTotal,
            rows: currentSkillData
        };

        return (
            <div style={{ overflowX: 'auto' }}>
            <table className="skill-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '3%' }} />
                <col style={{ width: '40%' }} />
                <col style={{ width: '42%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '5%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ textAlign: 'center' }}>#</th>
                  <th rowSpan={2} style={{ textAlign: 'left' }}>
                    <div style={{ marginBottom: '0.375rem', fontSize: '1rem' }}>Indicator</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.875rem', fontWeight: 400, color: 'var(--slate-500)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><span style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', backgroundColor: 'var(--blue-100)', border: '1px solid var(--blue-200)' }}></span> strong point</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><span style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', backgroundColor: 'var(--amber-100)', border: '1px solid var(--amber-200)' }}></span> weak point</span>
                    </div>
                  </th>
                  <th rowSpan={2} style={{ textAlign: 'left' }}>Skill</th>
                  <th rowSpan={2}>Score</th>
                  <th colSpan={2} style={{ padding: '0.25rem' }}>Percentage(%)</th>
                </tr>
                <tr>
                  <th>Student</th>
                  <th>Average</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentSkillData.map((row : ISkillAnalysisRecord, index : number) => {
                  const { rowspanMap, skipRows } = indicatorRowspanInfo;
                  const shouldRenderIndicator = !skipRows.has(index);
                  const rowspanCount = rowspanMap[index] || 1;
                  
                  return (
                  <tr key={row.row_id}  style={{ textAlign: 'center' }}>
                    {shouldRenderIndicator && (
                      <td 
                        style={{ color: 'var(--slate-500)', verticalAlign: 'middle' }} 
                        rowSpan={rowspanCount}
                      >
                        {Object.keys(rowspanMap).indexOf(String(index)) + 1}
                      </td>
                    )}
                    {shouldRenderIndicator && (
                      <td 
                        style={{ textAlign: 'left', verticalAlign: 'middle' }}
                        rowSpan={rowspanCount}
                        className={
                          row.indicator_name === weakIndicator ? 'skill-table-row-weak ' : 
                          row.indicator_name === strongIndicator ? 'skill-table-row-strong ' : ''
                        }
                      >
                        <div style={{ fontWeight: 700, color: 'var(--slate-700)', marginBottom: '0.125rem' }}>{row.indicator_name_eng}</div>
                        <div style={{ color: 'var(--slate-500)', fontSize: '0.875rem' }}>{row.indicator_name}</div>
                      </td>
                    )}
                    <td style={{ textAlign: 'left', color: 'var(--slate-600)' }}>
                        <div style={{ fontWeight: 700, color: 'var(--slate-700)', marginBottom: '0.125rem' }}>{row.skill_name_eng}</div>
                        <div style={{ color: 'var(--slate-500)', fontSize: '0.875rem' }}>{row.skill_name}</div>
                    </td>
                    <td 
                      className={row.total_questions !== row.student_score && activeTab !== 'TT' ? "skill-link" : ""}
                      onClick={() => {
                        if(row.total_questions === row.student_score || activeTab === 'TT') return;
                        setActivePopup({
                          type: 'WRONG_ANSWER',
                          data: {
                            exam_test_code: skillData?.exam_test_code || '',
                            skill_code: row.skill_code
                          }
                        });
                      }}
                      title={row.total_questions !== row.student_score ? "오답 문항 상세보기" : ""}
                    >
                      {row.student_score + '/' + row.total_questions}
                    </td>
                    <td style={{ fontWeight: 700 }}>{row.student_percentage}</td>
                    <td style={{ color: 'var(--slate-500)' }}>{row.avg_percentage}</td>
                  </tr>
                  );
                })}
                <tr style={{ fontWeight: 700, backgroundColor: 'rgba(248, 250, 252, 0.5)' }}>
                   <td colSpan={3} style={{ textAlign: 'right', color: 'var(--slate-700)' }}>Total</td>
                   <td style={{ textAlign: 'center', color: 'var(--slate-800)' }}>{data.total.totalQuestions}</td>
                   <td style={{ textAlign: 'center', fontWeight: 700 }}>{data.total.avgStudentPercentage}</td>
                   <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{data.total.avgAvgPercentage}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
    }

    // 점수에 따른 색상 클래스
    const getScoreColorClass = (score: number): string => {
        if (score >= 80) return 'care-text-blue';
        return 'care-text-red';
    };

    // 점수 색상 가져오기
    const getScoreColor = (assess_level: string | undefined): string => {
        if (assess_level === 'Advanced') return '#2563eb'; // blue-600
        if (assess_level === 'On') return '#374151'; // gray-700
        if (assess_level === 'Developing') return '#ef4444'; // red-500
        return 'var(--slate-800)'; // 기본 색상
    };

    // 배지 렌더링 함수 (더 눈에 띄게)
    const renderAssessLevelBadge = (assess_level: string | undefined) => {
        if (!assess_level) return null;
        
        let bgColor = '';
        let textColor = '#ffffff';
        
        if (assess_level === 'Advanced') {
            bgColor = '#2563eb'; // blue-600
        } else if (assess_level === 'Developing') {
            bgColor = '#ef4444'; // red-500
        } else if (assess_level === 'On') {
            bgColor = '#374151'; // gray-700
        } else {
            return null;
        }
        
        return (
            <span
                style={{
                    display: 'inline-block',
                    backgroundColor: bgColor,
                    color: textColor,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.375rem',
                    fontSize: '11px',
                    fontWeight: 500,
                    marginLeft: '1px',
                    lineHeight: '1.2',
                    whiteSpace: 'nowrap'
                }}
            >
                {assess_level}
            </span>
        );
    };

    // 학습 종합 진단 & 개선 전략 테이블 렌더링
    const renderDiagnosisTable = () => {
        const diagnosisData = displayLearningDiagnosisData;

        // 데이터가 없으면 테이블을 렌더링하지 않음
        if (!diagnosisData || !diagnosisData.exam_scores?.length) {
            return null;
        }

        // Map으로 변환하여 성능 최적화 (O(1) 조회)
        const examScoreMap = new Map(
            diagnosisData?.exam_scores?.map(s => [s.subject_name, s]) || []
        );
        const activityScoreMap = new Map(
            diagnosisData?.activity_scores?.map(s => [s.subject_type, s]) || []
        );
        const guidanceMap = new Map(
            diagnosisData?.subject_learning_guidances?.map(s => [s.subject_name, s]) || []
        );

        // 실제 데이터에 있는 과목들 추출 (exam_scores 또는 activity_scores에 있는 것만)
        const examSubjects = new Set(examScoreMap.keys());
        const activitySubjects = new Set(activityScoreMap.keys());
        const allSubjects = new Set([...examSubjects, ...activitySubjects]);
        
        // SUBJECT_ORDER 순서대로 정렬하되, 실제 데이터에 있는 것만 필터링
        const availableSubjects = SUBJECT_ORDER.filter(subject => allSubjects.has(subject));

        // 과목별 시험 점수 데이터 매핑 (Map 사용)
        const getExamScore = (subjectName: string) => {
            return examScoreMap.get(subjectName);
        };

        // 과목별 활동 점수 데이터 매핑 (Map 사용)
        const getActivityScore = (subjectType: string) => {
            return activityScoreMap.get(subjectType);
        };

        // 중점 관리 과목 선정 (레벨별 가중치 기반)
        let weakSubjects = new Set<string>();
        if (diagnosisData?.exam_scores?.length) {
            // 레벨 코드 확인 (첫 번째 과목의 level_code 사용)
            const levelCode = diagnosisData.exam_scores[0]?.level_code;
            
            // Deca, Hendeca는 제외
            if (levelCode && levelCode !== LEVEL_CODE.DECA && levelCode !== LEVEL_CODE.HENDECA) {
                // 해당 레벨의 가중치 정보 가져오기
                const levelConfig = WEAK_SUBJECT_WEIGHTS[levelCode];
                if (levelConfig) {
                    // 우선순위: Developing → On → Advanced 중 "존재하는 레벨"에서만 중점관리과목 선정
                    // (가중치 대상 과목만, 100점은 제외)
                    const candidatesAll = diagnosisData.exam_scores.filter(score =>
                        levelConfig.weights[score.subject_name] !== undefined &&
                        score.score_percentage < 100
                    );

                    const levelPriority: Array<'Developing' | 'On' | 'Advanced'> = ['Developing', 'On', 'Advanced'];
                    let chosenCandidates = candidatesAll;
                    for (const level of levelPriority) {
                        const subset = candidatesAll.filter(s => s.assess_level === level);
                        if (subset.length > 0) {
                            chosenCandidates = subset;
                            break;
                        }
                    }

                    // 점수 낮은 순으로 정렬, 같은 점수면 가중치 높은 순으로 정렬
                    const sortedScores = [...chosenCandidates].sort((a, b) => {
                        if (a.score_percentage !== b.score_percentage) {
                            return a.score_percentage - b.score_percentage;
                        }
                        const weightA = levelConfig.weights[a.subject_name] || 0;
                        const weightB = levelConfig.weights[b.subject_name] || 0;
                        return weightB - weightA;
                    });

                    const selected = sortedScores
                        .slice(0, levelConfig.count)
                        .map(score => score.subject_name);
                    
                    weakSubjects = new Set(selected);
                }
            }
        }

        // 약점 과목 여부 (레벨별 가중치 기반 선정)
        const isWeakSubject = (subjectName: string) => {
            return weakSubjects.has(subjectName);
        };

        // 중점 스킬 텍스트 가져오기 (weak_skills에서 skill_name 추출)
        const getFocusSkills = (subjectName: string) => {
            const examScore = getExamScore(subjectName);
            if (!examScore?.weak_skills?.length) return '-';
            return examScore.weak_skills.map(s => s.skill_name).join(', ');
        };

        // 학습 전략 가져오기 (약점 과목에만 표시) (Map 사용)
        const getStrategy = (subjectName: string) => {
            if (!isWeakSubject(subjectName)) return '-';
            const guidance = guidanceMap.get(subjectName);
            return guidance?.learning_recommendation || '-';
        };

        // 전체 만점 여부 확인 (모든 과목이 100점인 경우)
        const isAllPerfect = diagnosisData?.exam_scores?.every(score => score.score_percentage === 100) || false;

        return (
            <div className="p-3 bg-slate-50 rounded-lg">
                <div style={{ overflowX: 'auto' }}>
                    <table className="diagnosis-table">
                    <colgroup>
                        <col className="header-col" />
                        {availableSubjects.map(subject => (
                            <col key={subject} style={{ width: `${80 / availableSubjects.length}%` }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                        <th>구분</th>
                        {availableSubjects.map(subject => (
                            <th key={subject}>
                                {subject}
                                {isWeakSubject(subject) && <span className="diagnosis-weak-label">(중점 관리 과목)</span>}
                            </th>
                        ))}
                        </tr>
                    </thead>
                    <tbody>
                        {/* Row 1: 시험 성적 */}
                        <tr>
                        <td style={{ fontWeight: 700, textAlign: 'left' }}>시험 성적</td>
                        {availableSubjects.map(subject => {
                            const score = getExamScore(subject);
                            return (
                                <td key={subject} style={{ textAlign: 'center' }}>
                                    {score ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 700, color: getScoreColor(score.assess_level) }}>{score.score_percentage}</span>
                                            {renderAssessLevelBadge(score.assess_level)}
                                        </div>
                                    ) : '-'}
                                </td>
                            );
                        })}
                        </tr>
                        {/* Row 2: 약점 스킬 (전체 만점이 아닐 때만 표시) */}
                        {!isAllPerfect && (
                        <tr>
                        <td style={{ fontWeight: 700, textAlign: 'left' }}>약점 스킬</td>
                        {availableSubjects.map(subject => {
                            const score = getExamScore(subject);
                            const isWeak = isWeakSubject(subject);
                            return (
                                <td key={subject} className={'diagnosis-text-sm text-slate-600'}>
                                    {getFocusSkills(subject)}
                                </td>
                            );
                        })}
                        </tr>
                        )}
                        
                        {/* Row 3: READI 수행률 */}
                        <tr>
                        <td style={{ fontWeight: 700, textAlign: 'left' }}>READI 수행률</td>
                        {availableSubjects.map(subject => {
                            const activity = getActivityScore(subject);
                            return (
                                <td key={subject} style={{ textAlign: 'center' }}>
                                    {activity ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 700, color: getScoreColor(activity.completion_assess_level) }}>{activity.completion_rate}%</span>
                                            {renderAssessLevelBadge(activity.completion_assess_level)}
                                        </div>
                                    ) : '-'}
                                </td>
                            );
                        })}
                        </tr>

                        {/* Row 4: READI 성취율 */}
                        <tr>
                        <td style={{ fontWeight: 700, textAlign: 'left' }}>READI 성취율</td>
                        {availableSubjects.map(subject => {
                            const activity = getActivityScore(subject);
                            return (
                                <td key={subject} style={{ textAlign: 'center' }}>
                                    {activity && activity.average_score != null ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 700, color: getScoreColor(activity.score_assess_level) }}>{activity.average_score}점</span>
                                            {renderAssessLevelBadge(activity.score_assess_level)}
                                        </div>
                                    ) : '-'}
                                </td>
                            );
                        })}
                        </tr>

                        {/* Row 5: Alex 수행률 */}
                        <tr>
                        <td style={{ fontWeight: 700, textAlign: 'left' }}>Alex 수행률</td>
                        <td colSpan={availableSubjects.length} style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, color: getScoreColor(diagnosisData?.alex_assess_level) }}>
                                    Week당 {diagnosisData?.alex_books_per_week}권
                                </span>
                                {renderAssessLevelBadge(diagnosisData?.alex_assess_level)}
                                <span style={{ color: 'var(--slate-600)' }}>
                                    (Week 당 목표 {diagnosisData?.alex_weekly_target}권)
                                </span>
                            </div>
                        </td>
                        </tr>

                        {/* Row 6: 학습 전략 (전체 만점이 아닐 때만 표시) */}
                        {!isAllPerfect && (
                        <tr>
                        <td style={{ fontWeight: 700, textAlign: 'left', backgroundColor: 'var(--slate-50)' }}>학습 전략</td>
                        {availableSubjects.map(subject => (
                            <td key={subject} className="diagnosis-text-sm text-slate-600">
                                {getStrategy(subject)}
                            </td>
                        ))}
                        </tr>
                        )}
                    </tbody>
                    </table>
                </div>
            </div>
        );
    }

    const getEdubaseImageUrl = (imageName: string, imageType: 'header' | 'problem' = 'header') => {
        // 파일명에서 숫자 추출 (예: ./520916-1.png → 520916)
        const match = imageName.match(/(\d+)-\d+\.png/);
        const baseUrl = imageType === 'header' ? edubaseUrl + '/Header/' : edubaseUrl + '/Problem/';
        if (!match) return baseUrl + imageName;
        
        const fullNumber = match[1]; // 520916
        const prefix = fullNumber.substring(0, 4); // 5209
        const middlePath = `${prefix}/${fullNumber}/`; // 5209/520916/
        
        // 파일명에서 ./ 또는 / 제거
        const cleanFileName = imageName.replace(/^\.?\//, '');
        
        return baseUrl + middlePath + cleanFileName;
    }

    const closePopup = () => setActivePopup({ type: null });

    const getNextLevelDisplayYn = () => {
        if(!LevelDispCourseCodeArray.includes(Number(studentInfo.course_code))) {
            setIsNextLevelOpen(false);
            return false;
        }
        
        // MT2, MT3, TT 점수가 있을 때만 true
        if(isMT3Level && activeTab === 'MT2' && examTestCodes.MT2) {
            setIsNextLevelOpen(true);
            return true;
        }
        if(isMT3Level && activeTab === 'MT3' && examTestCodes.MT3) {
            setIsNextLevelOpen(true);
            return true;
        }
        if(!isMT3Level && activeTab === 'MT2' && examTestCodes.MT2) {
            setIsNextLevelOpen(true);
            return true;
        }
        if(!isMT3Level && activeTab === 'TT' && examTestCodes.TT) {
            setIsNextLevelOpen(true);
            return true;
        }
        
        setIsNextLevelOpen(false);
        return false;
    }

    // 마크다운 bold 텍스트 파싱 (**텍스트** -> <strong>텍스트</strong>)
    const parseMarkdownBold = (text: string) => {
        const parts = text.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                const boldText = part.slice(2, -2);
                return <strong key={index} className="font-semibold text-slate-900">{boldText}</strong>;
            }
            return <span key={index}>{part}</span>;
        });
    };

    const parseMarkdownBoldLine = (text: string) => {
        const lines = text.split('\n');
        return lines.map((line, lineIndex) => {
            // -로 시작하는 줄은 p태그로 감싸기
            if (line.trim().startsWith('-')) {
                const parts = line.split(/(\*\*.*?\*\*)/g);
                const processedParts = parts.map((part, partIndex) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        const boldText = part.slice(2, -2);
                        return <strong key={partIndex} className="font-semibold text-slate-900">{boldText}</strong>;
                    }
                    return <span key={partIndex}>{part}</span>;
                });
                return <p key={lineIndex} className="mb-1">{processedParts}</p>;
            }
            
            // 일반 줄은 bold 처리만
            const parts = line.split(/(\*\*.*?\*\*)/g);
            const processedParts = parts.map((part, partIndex) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    const boldText = part.slice(2, -2);
                    return <strong key={partIndex} className="font-semibold text-slate-900">{boldText}</strong>;
                }
                return <span key={partIndex}>{part}</span>;
            });
            
            // 빈 줄은 <br />로 처리
            if (line.trim() === '') {
                return <br key={lineIndex} />;
            }
            
            return <span key={lineIndex}>{processedParts}</span>;
        });
    };

    // analysis_result 파싱 및 렌더링
    const renderAnalysisResult = (analysisResult: Record<string, any> | null) => {
        if (!analysisResult || Object.keys(analysisResult).length === 0) {
            console.log('renderAnalysisResult: empty or null', analysisResult);
            return null;
        }

        const sections = analysisResult.sections;

        if (!sections || typeof sections !== 'object') {
            return null;
        }

        return (
            <div className="space-y-6">
                <span className="care-badge-ai">AI Generated</span>
                
                {/* 지양 표현 */}
                {sections.avoid && (
                    <div>
                        <h3 className="modal-section-title">0. {sections.avoid.title || '지양 표현'}</h3>
                        <div className="summary-box warn">
                            {sections.avoid.avoid_example && Array.isArray(sections.avoid.avoid_example) && sections.avoid.avoid_example.length > 0 && (
                                <ul className="care-list-disc text-sm text-slate-700 pl-4 mb-3">
                                    {sections.avoid.avoid_example.map((example: string, idx: number) => (
                                        <li key={idx} className="leading-relaxed">{parseMarkdownBold(example.replace(/^["']|["']$/g, ''))}</li>
                                    ))}
                                </ul>
                            )}
                            {sections.avoid.avoid_summary && (
                                <div className="text-sm text-slate-700 leading-relaxed">
                                    {parseMarkdownBold(sections.avoid.avoid_summary)}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 총평 */}
                {sections.summary && (
                    <div>
                        <h3 className="modal-section-title">1. {sections.summary.title || '총평'}</h3>
                        <div className="text-sm whitespace-pre-line" style={{ lineHeight: '2', gap: '0.75rem' }}>
                            {parseMarkdownBoldLine(sections.summary.content || '')}
                        </div>
                    </div>
                )}

                {/* 데이터 해석 가이드 */}
                {sections.guide && (
                    <div>
                        <h3 className="modal-section-title">2. {sections.guide.title || '데이터 해석 가이드'}</h3>
                        <div className="space-y-3">
                            {sections.guide.subjects && Array.isArray(sections.guide.subjects) && sections.guide.subjects.map((subject: any, idx: number) => (
                                <div key={idx} className="border-l-2 border-slate-200 pl-3">
                                    <div className="text-sm text-slate-700 leading-relaxed">
                                        <span className="font-semibold" style={{ color: 'var(--indigo-600)', minWidth: '100px' }}>{subject.label} : </span>
                                        {typeof subject.content === 'string' ? parseMarkdownBold(subject.content) : String(subject.content)}
                                    </div>
                                </div>
                            ))}
                            {sections.guide.counsel_point && (
                                <div className="summary-box note">
                                    <div className="font-semibold text-slate-800 mb-1" style={{ color: 'var(--indigo-600)', minWidth: '100px' }}>{sections.guide.counsel_point.label || '상담 Point'} : </div>
                                    <div className="text-sm text-slate-700 leading-relaxed">
                                        {typeof sections.guide.counsel_point.content === 'string' ? parseMarkdownBold(sections.guide.counsel_point.content) : String(sections.guide.counsel_point.content)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 행동 */}
                {sections.behavior && (
                    <div>
                        <h3 className="modal-section-title">3. {sections.behavior.title || '행동'}</h3>
                        <div className="summary-box warn">
                            {sections.behavior.acts && Array.isArray(sections.behavior.acts) && sections.behavior.acts.map((act: any, actIdx: number) => (
                                <div key={actIdx} className={actIdx > 0 ? 'mt-3' : ''}>
                                    <div className="font-semibold text-slate-800 mb-2" style={{ color: '#991b1b' }}>{act.label}</div>
                                    {act.items && Array.isArray(act.items) && (
                                        <ul className="care-list-disc text-sm text-slate-700 pl-4">
                                            {act.items.map((item: string, idx: number) => (
                                                <li key={idx} className="leading-relaxed">{parseMarkdownBold(item)}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 마무리 */}
                {sections.conclude && (
                    <div>
                        <h3 className="modal-section-title">4. {sections.conclude.title || '마무리'}</h3>
                        <div className="summary-box info">
                            {sections.conclude.closing && Array.isArray(sections.conclude.closing) && sections.conclude.closing.map((closing: any, closingIdx: number) => (
                                <div key={closingIdx} className={closingIdx > 0 ? 'mt-3' : ''}>
                                    <div className="font-semibold text-slate-800 mb-2" style={{ color: '#1e40af' }}>{closing.label}</div>
                                    {closing.items && Array.isArray(closing.items) && (
                                        <ul className="care-list-disc text-sm text-slate-700 pl-4">
                                            {closing.items.map((item: any, idx: number) => (
                                                <li key={idx} className="leading-relaxed">
                                                    <span className="font-medium">{item.label || ''}</span>
                                                    {item.detail && <span className="ml-2 text-slate-600">{item.detail}</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                            {sections.conclude.finalize && (
                                <div style={{ paddingTop: '0.5rem', borderTop: '1px solid #bfdbfe', marginTop: '0.5rem', color: '#1e3a8a', fontWeight: '500' }}>
                                    <div className="font-semibold mb-1">{sections.conclude.finalize.label || '마무리 멘트'}</div>
                                    {parseMarkdownBold(sections.conclude.finalize.content || '')}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // AI 총평 및 데이터 해석 상담 가이드 렌더링
    const renderAIAnalysisGuide = () => {
        const guideData = displayAIAnalysisGuideData;
        
        if (!guideData || !guideData.results || guideData.results.length === 0) {
            return null;
        }

        // analysis_result 파싱 (새로운 구조: sections 객체)
        const parseAnalysisResult = (result: any): Record<string, any> | null => {
            if (!result) {
                return null;
            }
            
            // 이미 객체인 경우
            if (typeof result === 'object' && !Array.isArray(result) && result !== null) {
                // sections가 있는지 확인
                if (result.sections && typeof result.sections === 'object') {
                    return result;
                }
                return null;
            }
            
            // 문자열인 경우 JSON 파싱 (역슬래시 포함 문자열도 안전하게 처리)
            if (typeof result === 'string') {
                try {
                    const parsed = JSON.parse(result);
                    // 파싱 결과가 객체이고 sections가 있는지 확인
                    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                        if (parsed.sections && typeof parsed.sections === 'object') {
                            return parsed;
                        }
                    }
                    console.error('Parsed result does not have sections:', typeof parsed);
                    return null;
                } catch (e) {
                    console.error('Failed to parse analysis_result:', e);
                    return null;
                }
            }
            
            return null;
        };

        return (
            <div className="p-3 bg-slate-50 rounded-lg">
                {guideData.results.map((item, index) => {
                    const parsedResult = parseAnalysisResult(item.analysis_result);
                    return (
                        <>
                            {parsedResult && Object.keys(parsedResult).length > 0 && (
                                <>
                                    {renderAnalysisResult(parsedResult)}
                                </>
                            )}
                        </>
                    );
                })}
            </div>
        );
    }

    return (
        <>
            <section id="section-analysis" className="care-card">
                {/* Tabs */}
                <div className="tabs-header">
                    {examTabs.map((tab) => {
                        // MT2, MT3, TT는 examTestCodes 값이 있을 때만 표시
                        if (tab === 'MT2' && !examTestCodes.MT2) {
                            return null;
                        }
                        if (tab === 'MT3' && !examTestCodes.MT3) {
                            return null;
                        }
                        if (tab === 'TT' && !examTestCodes.TT) {
                            return null;
                        }
                        return (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`tab-btn ${activeTab === tab ? 'active' : 'inactive'}`}
                            >
                                {tab}
                            </button>
                        );
                    })}
                </div>
                
                <div className="care-sub-body">
                    {/* 성적 변화(Chart) & 최근 성적 상세(Table Area) */}
                    <div className="grid-cols-2 pb-4">
                        {/* Chart Card */}
                        <div className="care-card h-full care-no-shadow">
                            <div className="card-header-dark">
                            <div className="card-title-row">
                                <TrendingUp size={22} color="white"/>
                                <h3 className="card-title text-white">성적 변화</h3>
                            </div>
                            </div>
                
                            <div className="care-card-body flex flex-col">
                            {activeTab === 'TT' ? (
                                displayTrendLTData?.previous_exam?.subjects && displayTrendLTData.previous_exam.subjects.length > 0 ? (    
                                <div className="h-full flex flex-col items-center justify-center" style={{ padding: '1rem' }}>
                                <div className="w-full h-full flex flex-col">
                                    <div className="care-indigo-header-row">
                                    <Layout size={18} style={{ color: 'var(--indigo-700)' }}/>
                                    <h4 className="care-m-0 font-bold" style={{ color: 'var(--indigo-700)' }}>{examTrendLTTitle + ' 성적 상세'} </h4>
                                    </div>
                                    <div style={{ border: '1px solid var(--slate-200)', borderRadius: '0 0 0.25rem 0.25rem' }}>
                                    <table className="care-table-custom">
                                        <thead>
                                        <tr>
                                            <th rowSpan={2} className="care-border-right">과목</th>
                                            <th rowSpan={2} className="care-text-center-border-right">총 문항</th>
                                            <th rowSpan={2} className="care-text-center-border-right">정답 수</th>
                                            <th colSpan={3} className="care-text-center" style={{ borderBottom: '1px solid var(--slate-200)' }}>Percentage (%)</th>
                                        </tr>
                                        <tr>
                                            <th className="care-table-header-small">점수</th>
                                            <th className="care-table-header-small">학급 평균</th>
                                            <th className="care-table-header-center">전 캠퍼스 평균</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {
                                            sortSubjectsByTableOrder(displayTrendLTData?.previous_exam?.subjects || []).map((row, i) => (
                                                <tr key={i} style={i === 0 ? { backgroundColor: 'var(--blue-50)', fontWeight: 700 } : {}}>
                                                    <td>{row.subject_name}</td>
                                                    <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{row.total_count}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{row.correct_count}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{row.score_percentage}</td>
                                                    <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{row.class_avg_percentage}</td>
                                                    <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{row.campus_avg_percentage}</td>
                                                </tr>
                                            ))
                                        }
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                                </div>
                                ) : (
                                <div className="bg-slate-50 care-chart-container">
                                    <div className="flex items-center justify-center h-full text-slate-400">
                                        데이터가 없습니다
                                    </div>
                                </div>
                                )
                            
                            ) : (
                                <>
                                <div className="flex care-flex-wrap-gap">
                                    {getChartData().availableSubjects.map(subject => (
                                    <label key={subject} className="bg-slate-50" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--slate-200)' }}>
                                        <input 
                                        type="checkbox" 
                                        checked={selectedSubjects.includes(subject)}
                                        onChange={() => toggleSubject(subject)}
                                        style={{ width: '1rem', height: '1rem', accentColor: SUBJECT_COLORS[subject] }}
                                        />
                                        <span className="font-medium text-sm" style={{ color: selectedSubjects.includes(subject) ? SUBJECT_COLORS[subject] : '#64748b' }}>
                                        {subject}
                                        </span>
                                    </label>
                                    ))}
                                </div>

                                <div className="bg-slate-50 care-chart-container">
                                    {examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code && displayTrendMTData?.trends && displayTrendMTData.trends.length > 0 && getChartData().series.length > 0 ? (
                                        <Chart
                                            type="line"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    toolbar: { show: false },
                                                    zoom: { enabled: false }
                                                },
                                                xaxis: {
                                                    categories: getChartData().categories,
                                                    labels: {
                                                        style: { fontSize: '12px' }
                                                    }
                                                },
                                                yaxis: {
                                                    min: 0,
                                                    max: 100,
                                                    tickAmount: 4,
                                                    labels: {
                                                        style: { fontSize: '12px' },
                                                        formatter: (val: number) => Math.round(val).toString()
                                                    }
                                                },
                                                stroke: {
                                                    width: 2,
                                                    curve: 'straight',
                                                    dashArray: [0, 0, 5, 8, 3]  // Overall, Listening: 실선, Reading: 짧은점선, Vocabulary: 긴점선, Grammar: 짧은점선
                                                },
                                                markers: {
                                                    size: 5,
                                                    strokeWidth: 0,
                                                    hover: { size: 7 }
                                                },
                                                legend: {
                                                    show: false
                                                },
                                                grid: {
                                                    borderColor: '#e2e8f0'
                                                },
                                                tooltip: {
                                                    y: {
                                                        formatter: (val: number) => `${val}%`
                                                    }
                                                }
                                            }}
                                            series={getChartData().series}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-slate-400">
                                            데이터가 없습니다
                                        </div>
                                    )}
                                </div>
                                </>
                            )}
                            </div>
                        </div>

                        {/* Table Card */}
                        <div className="care-card h-full care-no-shadow" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="card-header-dark">
                            <div className="card-title-row">
                                <Layout size={22} color="white"/>
                                <h3 className="card-title text-white">{examDetailTitle + ' 성적 상세'}</h3>
                            </div>
                            </div>
                            <div className="card-body care-no-padding" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <table className="care-subtable-custom" style={{ flex: 1 }}>
                                <thead>
                                <tr>
                                    <th rowSpan={2} className="care-border-right">과목</th>
                                    <th rowSpan={2} className="care-text-center-border-right">총 문항</th>
                                    <th rowSpan={2} className="care-text-center-border-right">정답 수</th>
                                    <th colSpan={3} className="care-text-center" style={{ borderBottom: '1px solid var(--slate-200)', padding: '5.25px 7px' }}>Percentage (%)</th>
                                </tr>
                                <tr>
                                    <th className="care-table-header-small" style={{ padding: '5.25px 7px' }}>점수</th>
                                    <th className="care-table-header-small" style={{ padding: '5.25px 7px' }}>학급 평균</th>
                                    <th className="care-table-header-center" style={{ padding: '5.25px 7px' }}>전 캠퍼스 평균</th>
                                </tr>
                                </thead>
                                <tbody>
                                {getTableDataWithOverall().map((row, i) => (
                                    <tr key={i} style={i === 0 ? { backgroundColor: 'var(--blue-50)', fontWeight: 700 } : {}}>
                                    <td>{row.subject_name}</td>
                                    <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{row.total_count}</td>
                                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{row.correct_count}</td>
                                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{row.score_percentage}</td>
                                    <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{row.class_avg_percentage}</td>
                                    <td style={{ textAlign: 'center', color: 'var(--slate-500)' }}>{row.campus_avg_percentage}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>

                    {/* Accordions */}
                    {/* 스킬 유형 분석 (정오답 문항 분석) */}
                    <div id='section-skill' key='skill' className="accordion-item">
                        <button 
                            onClick={() => toggleAccordion('skill' as keyof typeof accordions)}
                            className="accordion-trigger"
                        >
                        <div className="flex items-center gap-3">
                            <ArrowRight className="text-white" size={20} />
                            <span className="font-bold text-white care-text-xl">{'스킬 유형 분석 (정오답 문항 분석)'}</span>
                        </div>
                        {accordions['skill' as keyof typeof accordions] ? <ChevronUp size={20} className="text-white"/> : <ChevronDown size={20} className="text-white"/>}
                        </button>
                        {accordions['skill' as keyof typeof accordions] && (
                        <div className="accordion-content">
                            <div className="accordion-content-inner">
                                <div className="care-skill-container">
                                    {availableSkillSubjects.length > 0 && (
                                    <div className="flex gap-2 care-skill-tabs">
                                        {availableSkillSubjects.map(sub => (
                                        <button 
                                            key={sub} 
                                            onClick={() => setActiveSkillTab(sub)}
                                            className={`care-skill-tab-btn ${activeSkillTab === sub ? 'active' : 'inactive'}`}
                                        >
                                            <span style={{ color: activeSkillTab === sub ? 'var(--brand-pink)' : 'var(--white)' }}>{sub}</span>
                                        </button>
                                        ))}
                                    </div>
                                    )}
                                    {examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code && 
                                     displaySkillAnalysisData?.exam_test_code === examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code &&
                                     currentSkillData.length > 0 && skillTotal !== null ? (
                                        renderSkillTable()
                                    ) : (
                                        <div className="bg-slate-50 care-empty-container">
                                            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--slate-400)' }}>
                                                데이터가 없습니다
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                    {/* 학습 종합 진단 & 개선 전략 */}
                    <div id='section-diagnosis' key='diagnosis' className="accordion-item">
                        <button 
                            onClick={() => toggleAccordion('diagnosis' as keyof typeof accordions)}
                            className="accordion-trigger"
                        >
                        <div className="flex items-center gap-3">
                            <CheckCircle className="text-white" size={20} />
                            <span className="font-bold text-white" style={{ fontSize: '17px' }}>{'학습 종합 진단 & 개선 전략'}</span>
                        </div>
                        {accordions['diagnosis' as keyof typeof accordions] ? <ChevronUp size={20} className="text-white"/> : <ChevronDown size={20} className="text-white"/>}
                        </button>
                        {accordions['diagnosis' as keyof typeof accordions] && (
                        <div className="accordion-content">
                            <div className="accordion-content-inner">
                                <div style={{ backgroundColor: 'var(--slate-50)', borderRadius: '0.5rem' }}>
                                    {learningDiagnosisLoading ? <div style={{ position: 'relative', height: '170px'}}><Spinner /></div> : null}
                                    {learningDiagnosisLoading ? null : (
                                        examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code && 
                                        displayLearningDiagnosisData?.exam_test_code === examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code &&
                                        displayLearningDiagnosisData?.exam_scores && displayLearningDiagnosisData.exam_scores.length > 0 ? (
                                            renderDiagnosisTable()
                                        ) : (
                                            <div className="care-skill-container">
                                                <div className="bg-slate-50 care-empty-container ">
                                                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--slate-400)' }}>
                                                        데이터가 없습니다
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                    {/* AI 총평 및 데이터 해석 가이드 */}
                    <div id='section-summary' key='summary' className="accordion-item">
                        <button 
                            onClick={() => toggleAccordion('summary' as keyof typeof accordions)}
                            className="accordion-trigger"
                        >
                        <div className="flex items-center gap-3">
                            <FileText className="text-white" size={20} />
                            <span className="font-bold text-white" style={{ fontSize: '17px' }}>{'AI 총평 및 데이터 해석 상담 가이드'}</span>
                        </div>
                        {accordions['summary' as keyof typeof accordions] ? <ChevronUp size={20} className="text-white"/> : <ChevronDown size={20} className="text-white"/>}
                        </button>
                        {accordions['summary' as keyof typeof accordions] && (
                        <div className="accordion-content">
                            <div className="accordion-content-inner">
                                {aiAnalysisGuideLoading ? <div style={{ position: 'relative', height: '170px'}}><Spinner /></div> : null}
                                {aiAnalysisGuideLoading ? null : (
                                <div style={{ backgroundColor: 'var(--slate-50)', borderRadius: '0.5rem' }}>
                                    {examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code && 
                                     displayAIAnalysisGuideData?.exam_test_code === examTestCodes[activeTab as keyof typeof examTestCodes]?.exam_test_code &&
                                     displayAIAnalysisGuideData?.results && displayAIAnalysisGuideData.results.length > 0 ? (
                                        renderAIAnalysisGuide()
                                    ) : (
                                        <div className="care-skill-container">                          
                                        {
                                            <div className="bg-slate-50 care-empty-container ">
                                            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--slate-400)' }}>
                                                데이터가 없습니다
                                            </div>
                                            </div>
                                        }                           
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                    {/* 다음 레벨 정보 */}
                    {getNextLevelDisplayYn() && (
                    <div id='section-nextLevel' key='nextLevel' className="accordion-item">
                        <button 
                            onClick={() => toggleAccordion('nextLevel' as keyof typeof accordions)}
                            className="accordion-trigger"
                        >
                        <div className="flex items-center gap-3">
                            <TrendingUp className="text-white" size={20} />
                            <span className="font-bold text-white" style={{ fontSize: '17px' }}>{'다음 레벨 정보'}</span>
                        </div>
                        {accordions['nextLevel' as keyof typeof accordions] ? <ChevronUp size={20} className="text-white"/> : <ChevronDown size={20} className="text-white"/>}
                        </button>
                        {accordions['nextLevel' as keyof typeof accordions] && (
                        <div className="accordion-content">
                            <div className="accordion-content-inner">
                                {studentInfo && <NextLevel curCourseCode={studentInfo.course_code} curCourseName={studentInfo.course_name} />}
                            </div>
                        </div>
                        )}
                    </div>
                    )}
                </div>
            </section>

            <Modal isOpen={activePopup.type === 'WRONG_ANSWER'} onClose={closePopup} title="오답 문항 분석">
                <div className="flex flex-col gap-6">
                    {parsedWrongAnswerData.map((item, index) => (
                        <div key={index}>
                            <div style={{ backgroundColor: 'var(--slate-50)', padding: '14px', borderRadius: '7px', border: '1px solid var(--slate-200)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--slate-500)', backgroundColor: 'var(--slate-200)', padding: '4px 7px', borderRadius: '4px' }}>{item.subject_name + ' Q.' +  item.questionNum}</span>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--red-500)' }}>난이도: {item.difficulty_level}</span>
                </div>
                                {item.direction && (
                                    <p style={{ fontWeight: 600, fontSize: '14px', color: 'var(--slate-600)', marginBottom: '7px', margin: '0 0 7px 0' }}>{item.direction}</p>
                                )}
                                <p style={{ fontWeight: 500, fontSize: '16px', color: 'var(--slate-800)', marginBottom: '14px', margin: '0 0 14px 0' }}>{item.question}</p>
                                {item.context && item.context.length > 0 && (
                                     <div style={{ marginBottom: '14px', padding: '11px', backgroundColor: 'var(--white)', borderRadius: '4px', border: '1px solid var(--slate-200)' }}>
                                         <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--slate-500)', marginBottom: '7px' }}>지문</div>
                                         <div style={{ fontSize: '13px', color: 'var(--slate-700)', lineHeight: 1.6 }}>
                                             {item.context.map((ctx, idx) => (
                                                 <span key={idx}>{ctx} </span>
                                             ))}
                                         </div>
                                     </div>
                                 )}

                                 {/* 선택지 (이미지 유무에 따라 레이아웃 변경) */}
                                {item.contextImage ? (
                                    <div style={{ display: 'flex', gap: '14px', alignItems: 'stretch' }}>
                                        {/* Image Section (25% Width) */}
                                        <div style={{ width: '25%', position: 'relative', minHeight: '180px', borderRadius: '7px', overflow: 'hidden', border: '1px solid var(--slate-200)' }}>
                                            <img 
                                                src={getEdubaseImageUrl(item.contextImage, item.imageType || 'header')}
                                                alt="Question Image" 
                                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        </div>
                                        {/* Options Section (75% Width) */}
                                        <div style={{ width: '75%', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                            {item.choices.map((choice, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={
                                                        choice.isCorrect 
                                                            ? 'quiz-choice-correct' 
                                                            : choice.isUserAnswer 
                                                                ? 'quiz-choice-wrong' 
                                                                : 'quiz-choice'
                                                    }
                                                >
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <span>{String.fromCharCode(64 + choice.num)}.</span>
                                                        {choice.image ? (
                                                            <img 
                                                                src={getEdubaseImageUrl(choice.image, 'problem')} 
                                                                alt={`Choice ${String.fromCharCode(64 + choice.num)}`}
                                                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
                                                            />
                                                        ) : (
                                                            <span>{choice.text}</span>
                                                        )}
                                                        {choice.isCorrect && <span className="care-text-sm text-green-600 ml-1">(정답)</span>}
                                                        {choice.isUserAnswer && !choice.isCorrect && <span className="care-text-sm color-text-red-500 ml-1">(학생 선택)</span>}    
                                                    </span>
                                                    {item.answerDistributionParsed && item.answerDistributionParsed.length > 0 && (
                                                        <span style={{ fontSize: '12px', color: 'var(--slate-400)', fontWeight: 500 }}>{(item.answerDistributionParsed.find(item => item.answer === String.fromCharCode(64 + choice.num))?.rate ?? 0)}%</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                        {item.choices.map((choice, idx) => (
                                            <div 
                                                key={idx} 
                                                className={
                                                    choice.isCorrect 
                                                        ? 'quiz-choice-correct' 
                                                        : choice.isUserAnswer 
                                                            ? 'quiz-choice-wrong' 
                                                            : 'quiz-choice'
                                                }
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span>{String.fromCharCode(64 + choice.num)}.</span>
                                                    {choice.image ? (
                                                        <img 
                                                            src={getEdubaseImageUrl(choice.image, 'problem')} 
                                                            alt={`Choice ${String.fromCharCode(64 + choice.num)}`}
                                                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
                                                        />
                                                    ) : (
                                                        <span>{choice.text}</span>
                                                    )}
                                                    {choice.isCorrect && <span className="care-text-sm text-green-600 ml-1">(정답)</span>}
                                                    {choice.isUserAnswer && !choice.isCorrect && <span className="care-text-sm color-text-red-500 ml-1">(학생 선택)</span>}
                                                    
                                                </span>
                                                {item.answerDistributionParsed && item.answerDistributionParsed.length > 0 && (
                                                    <span style={{ fontSize: '12px', color: 'var(--slate-400)', fontWeight: 500 }}>{(item.answerDistributionParsed.find(item => item.answer === String.fromCharCode(64 + choice.num))?.rate ?? 0)}%</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    )
}

export default Analysis;